import { StyleSheet, View } from 'react-native';

import type { PageBuilderWidgetAlert } from '@fhs/cms-groq-queries';
import { Text, XStack, XStackProps, YStack, createMqStyles, tokens, useMqSelect } from '@fhs/ui';

import { ActionLink } from '../components/action-link';
import { Icon } from '../components/icon';
import { SimpleBlockContent } from '../components/simple-block-content';
import { ActionHandler } from '../types';
import { addActionSource } from '../utils/action';

export type WidgetAlertProps = Pick<
  PageBuilderWidgetAlert,
  'colorVariant' | 'alignment' | 'leadingIcon' | 'content' | 'trailingActions'
> & {
  style?: XStackProps['style'];
  onAction: ActionHandler;
};

export function WidgetAlert(props: WidgetAlertProps) {
  const mqStyles = useMqStyles();

  const iconSize = useMqSelect({ $gteDesktop: 24 }, 20);

  const textColor =
    props.colorVariant === 'dark' || props.colorVariant === 'success-highlight'
      ? tokens.colors.$white
      : tokens.colors.$houseDark;

  return (
    <XStack style={[mqStyles.container, bgStyles[props.colorVariant], props.style]}>
      <XStack style={mqStyles.widthContainer}>
        {props.alignment === 'center' && <Spacer />}

        {props.leadingIcon && <Icon size={iconSize} name={props.leadingIcon} color={textColor} />}
        <YStack style={mqStyles.contentContainer}>
          <SimpleBlockContent
            value={props.content}
            onAction={addActionSource(
              { component: 'content', componentType: 'block' },
              props.onAction
            )}
            normalText={{
              size: 'sm',
              style: { color: textColor, flexGrow: 1 },
            }}
            linkText={{
              size: 'sm',
              style: { color: textColor, flexGrow: 1, textDecorationLine: 'underline' },
            }}
          />
        </YStack>

        {props.alignment === 'left' && <Spacer />}

        {props.trailingActions?.map(action => {
          return (
            <ActionLink
              key={action._key}
              action={action.link}
              onAction={addActionSource(
                {
                  text: action.text,
                },
                props.onAction
              )}
              asChild
            >
              <Text.Paragraph
                size="xs"
                weight="semibold"
                style={{
                  paddingHorizontal: 8,
                  alignSelf: 'center',
                  color: textColor,
                }}
              >
                {action.text}
              </Text.Paragraph>
            </ActionLink>
          );
        })}

        {props.alignment === 'center' && <Spacer />}
      </XStack>
    </XStack>
  );
}

const useMqStyles = createMqStyles({
  container: {
    $base: {
      paddingVertical: 12,
      paddingHorizontal: 16,
    },
    $gteDesktop: {
      paddingVertical: 16,
      paddingHorizontal: 24,
    },
  },
  widthContainer: {
    $base: {
      gap: 8,
      maxWidth: 1336,
      flexBasis: '100%',
      marginHorizontal: 'auto',
    },
  },
  contentContainer: {
    $base: {
      flexShrink: 1,
      justifyContent: 'center',
    },
  },
  actionsContainer: {
    $base: {
      justifyContent: 'center',
    },
  },
});

const bgStyles = StyleSheet.create({
  default: {
    backgroundColor: tokens.colors.$houseLight,
  },
  dark: {
    backgroundColor: tokens.colors.$houseDark,
  },
  success: {
    backgroundColor: tokens.colors.$successBackground,
  },
  'success-highlight': {
    backgroundColor: tokens.colors.$success,
  },
  error: {
    backgroundColor: tokens.colors.$errorBackground,
  },
  warning: {
    backgroundColor: tokens.colors.$warningBackground,
  },
});

function Spacer() {
  return <View style={spacerStyles.spacer} />;
}

const spacerStyles = StyleSheet.create({
  spacer: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
  },
});
